<template lang="pug">
div
  // Use modern cards for a cleaner look
  div.modern-cards-wrapper(v-if="useModernCards && selectedCate")
    ModernPlanCards(
      :selectedCate="selectedCate"
      :selectedTime="selectedTime"
      :feeList="feeList"
      :calcPrice="calcPrice"
      :currentPlanId="currentPlanId"
      :modelValue="selectedPlan"
      @select-plan="selectPlan"
      @contact-enterprise="$emit('contact-enterprise')"
    )
  // Original pricing tables as fallback
  div(v-else)
    div.price-table(v-if="selectedCate === 'allplatform'")
      .plan-card.pro
        UnifiedPlanCard(
          :planId="4"
          :selectedPlan="selectedPlan"
          :currentPlanId="currentPlanId"
          :currentService="currentService"
          :selectedTime="selectedTime"
          :feeList="feeList"
          :calcPrice="calcPrice"
          :features="apPlan4Features"
          @select-plan="selectPlan"
        )
      .plan-card.basic
        UnifiedPlanCard(
          :planId="2"
          :selectedPlan="selectedPlan"
          :currentPlanId="currentPlanId"
          :currentService="currentService"
          :selectedTime="selectedTime"
          :feeList="feeList"
          :calcPrice="calcPrice"
          :features="apPlan2Features"
          @select-plan="selectPlan"
        )
    div.price-table(v-if="selectedCate === 'home'")
      .plan-card.pro
        UnifiedPlanCard(
          :planId="6"
          :selectedPlan="selectedPlan"
          :currentPlanId="currentPlanId"
          :currentService="currentService"
          :selectedTime="selectedTime"
          :feeList="feeList"
          :calcPrice="calcPrice"
          :features="homePlan6Features"
          @select-plan="selectPlan"
        )
      .plan-card.basic
        UnifiedPlanCard(
          :planId="5"
          :selectedPlan="selectedPlan"
          :currentPlanId="currentPlanId"
          :currentService="currentService"
          :selectedTime="selectedTime"
          :feeList="feeList"
          :calcPrice="calcPrice"
          :features="homePlan5Features"
          @select-plan="selectPlan"
        )
  div.hints(v-if="!useModernCards")
    h4.amount-currency-hint(v-if="selectedCate") {{$t('pricing.prices_shown')}}
      span {{$t('eur')}}
    h4.otp(v-if="selectedCate") {{$t('pricing.one-time-payment')}}
</template>

<script lang="ts" setup>
import UnifiedPlanCard from '~/components/pricing/UnifiedPlanCard.vue'
import ModernPlanCards from '~/components/pricing/ModernPlanCards.vue'
import { billedMonthsForDuration } from '@/utils/durationDiscount'

const $t = useI18n().t
const emit = defineEmits(['contact-enterprise'])

// Toggle to use modern cards - set to true to enable new design
const useModernCards = ref(true)
const pricing = usePricing()
const service = useUserService()
const selectedCate = computed(() => pricing.cate)
const selectedTime = computed(() => pricing.time)
const selectedPlan = computed(() => Number(pricing.plan ?? (pricing.cate === 'home' ? 6 : 4)))
const currentPlanId = computed(() => ((service as any)[selectedCate.value as string] || {})?.plan_id)
const discount = computed(() => {
  // if (props.currentPlanId && currentService.value?.status === 'on') {
  //   if (props.currentPlanId && selectedPlan.value !== props.currentPlanId) {
  //     return 1
  //   }
  // }
  const duration = selectedTime.value
  if (duration < 1) return 1
  return billedMonthsForDuration(duration) / duration
})
const feeList = computed(() => {
  // console.log('feeList in plan options:', useRuntimeConfig().public.feeList)
  return useRuntimeConfig().public.feeList
})
const currentService = computed(() => service.currentService)

// Define features for each plan
const apPlan2Features = computed(() => [
  { type: 'thin', text: $t('plan_desc.plan2_quota') },
  { beforeHighlight: $t('plan_desc.current_use'), highlight: '3', afterHighlight: $t('plan_desc.devices') },
  $t('plan_desc.all_devices_ready'),
  { beforeHighlight: $t('plan_desc.provide'), highlight: '1Gbps', afterHighlight: $t('plan_desc.china_optimized') },
  { highlight: 'IEPL/IPLC', afterHighlight: $t('plan_desc.intl_line') },
  $t('plan_desc.ssltide'),
  $t('plan_desc.protocol_support')
])

const apPlan4Features = computed(() => [
  { type: 'thin', text: $t('plan_desc.plan4_quota') },
  { beforeHighlight: $t('plan_desc.current_use'), highlight: '5', afterHighlight: $t('plan_desc.devices') },
  $t('plan_desc.everything_in_basic2'),
  $t('plan_desc.pro_only_server'),
  $t('plan_desc.pro_qos'),
  $t('plan_desc.remote_support')
])

const homePlan5Features = computed(() => [
  { type: 'thin', text: $t('plan_desc.plan5_quota') },
  { beforeHighlight: $t('plan_desc.current_use'), highlight: '3', afterHighlight: $t('plan_desc.devices') },
  $t('plan_desc.all_devices_ready'),
  $t('plan_desc.anyconnect'),
  $t('plan_desc.ssltide')
])

const homePlan6Features = computed(() => [
  { type: 'thin', text: $t('plan_desc.plan6_quota') },
  { beforeHighlight: $t('plan_desc.current_use'), highlight: '5', afterHighlight: $t('plan_desc.devices') },
  $t('plan_desc.everything_in_basic5'),
  $t('plan_desc.pro_only_server'),
  $t('plan_desc.remote_support')
])

function calcPrice(fullPrice) {
  return Math.floor(fullPrice * discount.value * 10) / 10
}
function selectPlan(plan) {
  pricing.plan = Number(plan)
  console.log('new plan:', plan)
  const details = pricing.calculateFeeDetails({
    selectedPlan: Number(plan),
    purchaseType: pricing.purchaseType,
  })
  if (details.amountDue > 0) {
    useScrollTo({ el: '#payment', offset: -200, mobileOnly: true })
  }
}
// console.log('props is:', props)
</script>

<style lang="postcss">
@reference "@/assets/css/tailwind.css";
.modern-cards-wrapper {
  @apply w-full;
  margin: 0;
  padding: 0;
}

.price-table {
  max-width: 1000px;
  margin: 3rem auto;
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
  }
  
  .plan-card {
    flex: 1;
    max-width: 500px;
    display: flex;
    
    &.basic {
      max-width: 450px;
    }
    
    &.pro {
      max-width: 500px;
    }
    
    @media (max-width: 768px) {
      max-width: 100%;
      width: 100%;
    }
    
    > * {
      width: 100%;
    }
  }

  h2 {
    font-size: 1.5rem;
  }

  .pricetag {
    * {
      text-align: center;
    }

    h1 {
      margin-top: 1.3rem;
      font-size: 2.5rem;
      position: relative;

      small {
        font-size: 1rem;
        font-weight: normal;
        color: var(--secondary-color);
        margin: 0 5px;
      }
      
      .original-price {
        position: relative;
        color: var(--secondary-color);
        opacity: 0.6;
        font-size: 1.8rem;
        margin-right: 0.75rem;
        font-weight: 400;
        text-decoration: line-through;
        text-decoration-color: rgba(239, 68, 68, 0.5);
        text-decoration-thickness: 2px;
      }
      
      .discounted-price {
        position: relative;
        color: #7ed321;
        font-weight: 800;
        font-size: 2.6rem;
        display: inline-block;
        text-shadow: 0 2px 8px rgba(126, 211, 33, 0.2);
        animation: subtle-pulse 3s ease-in-out infinite;
        
        .light & {
          color: #059862;
          text-shadow: 0 2px 8px rgba(5, 152, 98, 0.15);
        }
      }
      
      @keyframes subtle-pulse {
        0%, 100% {
          transform: scale(1);
          opacity: 1;
        }
        50% {
          transform: scale(1.02);
          opacity: 0.95;
        }
      }
    }
  }

  .content {
    width: 320px;
    margin: 0.5rem auto;
    text-align: left;

    /* padding: 20px; */
    h5 {
      padding-left: 20px;
      font-weight: 500;
      font-size: 14px;
      width: 100%;
      position: relative;
      display: block;
      color: var(--secondary-color);
      margin: 10px 0;

      span {
        color: var(--secondary-color);
      }

      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        top: 0;
      }

      &.plus::before {
        content: '✚';
        position: absolute;
        left: 0;
        top: 0;
      }
    }
  }
}

.hints {
  margin-top: 1rem;
  text-align: center;

  .amount-currency-hint {
    text-align: center;
  }
}
</style>
